/**
 * SSO Callback Page
 *
 * Handles the OAuth2/OIDC redirect callback from identity providers.
 * Extracts code + state from URL query params, exchanges them for tokens,
 * and redirects to the dashboard on success.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ssoService } from '../../../api/sso.service';
import { useAuth } from '../../../contexts/AuthContext';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Spinner } from '../../../components/ui/Spinner';
import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import logoLgImage from '../../../assets/logo-lg.png';

export const SSOCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false);
  const { completeSSOLogin } = useAuth();

  useEffect(() => {
    // Prevent double-processing in React strict mode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const iss = searchParams.get('iss');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle IdP-level errors (e.g., user denied access)
      if (errorParam) {
        setError(errorDescription || `Authentication failed: ${errorParam}`);
        setIsProcessing(false);
        return;
      }

      // Validate required params
      if (!code || !state) {
        setError('Invalid callback: missing authorization code or state parameter.');
        setIsProcessing(false);
        return;
      }

      try {
        // Exchange code + state for tokens
        const response = await ssoService.callback({ code, state, iss: iss || undefined });

        console.log('[SSO] Callback successful:', {
          userId: response.user.userId,
          tenantCode: response.tenant.tenantCode,
        });

        // Use AuthContext to save tokens and update state
        completeSSOLogin(response);

        // Redirect to dashboard
        navigate('/app/dashboard', { replace: true });
      } catch (err) {
        const message = getErrorMessage(err);
        console.error('[SSO] Callback failed:', message);

        if (message.toLowerCase().includes('state')) {
          setError('SSO session expired or invalid. Please try signing in again.');
        } else if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('no account')) {
          setError('No account found for this identity. Please contact your administrator.');
        } else if (message.toLowerCase().includes('domain')) {
          setError('Your email domain is not allowed for this organization.');
        } else {
          setError(message || 'An error occurred during SSO authentication. Please try again.');
        }
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, completeSSOLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="flex justify-center">
          <img src={logoLgImage} alt="Ketrics" className="h-32 w-auto" />
        </div>

        {isProcessing ? (
          /* Processing state */
          <div className="text-center space-y-4">
            <Spinner size="lg" />
            <div>
              <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Completing sign-in...
              </h2>
              <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Please wait while we verify your identity.
              </p>
            </div>
          </div>
        ) : (
          /* Error state */
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Sign-in Failed
              </h2>
            </div>

            <Alert variant="error">
              {error}
            </Alert>

            <div className="flex flex-col gap-3">
              <Link to="/login">
                <Button variant="primary" size="lg" fullWidth>
                  Back to Sign In
                </Button>
              </Link>
              <div className="text-center">
                <a
                  href="mailto:support@ketrics.com"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Contact support
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOCallbackPage;
